<template>
  <category-dialog-card
    v-touch-hold.mouse="handleTouchHold"
    :item="item"
    :item-width="itemWidth"
    :dialog="dialog"
    :hide-image="hideImage"
    :bg-color="bgColor"
    :text-color="textColor"
    :is-mobile="isMobile"
    :item-name="itemName"
    :text-center="textCenter"
    data-test-id="item-card"
    :item-count="itemCount(item)"
    :has-reduced-opacity="hasReducedOpacity(item, { order })"
    :is-touching="isTouching"
    :badge-text-color="badgeTextColor"
    :display-price="displayPrice"
    :item-price="itemPrice"
    :has-extra-charge="hasExtraCharge"
    @touchstart="handleTouchStart"
    @click="handleClick"
  >
    <template #availability-badge>
      <q-badge
        v-if="isAvailabilityBadgeVisible(item, { order })"
        class="absolute-top-right"
        style="opacity: 1;"
        :color="availabilityBadgeColor(item, { order })"
        :label="availableStock(item, { order })"
      />
    </template>
    <template #alcohol-dialog>
      <alcohol-acknowledgement-dialog
        ref="alcoholAcknowledgementDialog"
        @alcohol-acknowledgement-concerned="checkItemSalesRestrictions()"
      />
    </template>
    <template #sales-dialog>
      <toggle-sales-disabled-dialog
        ref="toggleSalesDisabledDialog"
        :item="item"
        @request-authorization="callback => $emit('request-authorization', callback)"
      />
    </template>
  </category-dialog-card>
</template>

<script>
import { Cookies, Notify } from 'quasar'
import Item from 'src/models/item/Item'
import Order from 'src/models/order/Order'
import AlcoholAcknowledgementDialog from 'src/modules/checkout/components/AlcoholAcknowledgementDialog'
import CategoryDialogCard from 'src/modules/checkout/components/CategoryDialogCard'
import ToggleSalesDisabledDialog from 'src/modules/checkout/components/ToggleSalesDisabledDialog'
import { useSalesRestrictions } from 'src/modules/register/composables/sales-restrictions/sales-restrictions'
import { availabilityBadgeColor, availableStock, hasReducedOpacity, isAvailabilityBadgeVisible, isOutOfStock } from 'src/services/inventory/sales-restrictions'

export default {
  name: 'ItemCard',
  components: {
    CategoryDialogCard,
    AlcoholAcknowledgementDialog,
    ToggleSalesDisabledDialog
  },
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite'],
  props: {
    item: {
      type: Item,
      required: true
    },
    order: {
      type: Order,
      required: false,
      default: null
    },
    displayPrice: {
      type: Boolean,
      required: false,
      default: true
    },
    itemWidth: {
      type: String,
      required: false,
      default: '18%'
    },
    dialog: {
      type: Boolean,
      required: false,
      default: true
    },
    hideImage: {
      type: Boolean,
      required: false,
      default: false
    },
    bgColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    textColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false
    },
    hasAlcoholAcknowledgement: {
      type: Boolean,
      required: false,
      default: false
    },
    itemName: {
      type: String,
      required: false,
      default: null
    },
    textCenter: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['click', 'request-authorization'],
  setup () {
    const { checkForSalesRestrictions } = useSalesRestrictions()

    return {
      checkForSalesRestrictions
    }
  },
  data () {
    return {
      itemPrice: null,
      hasExtraCharge: false,
      isTouching: false
    }
  },
  computed: {
    inventoryStrategy () {
      return this.currentSite?.inventoryStrategy
    },
    badgeTextColor () {
      if (this.hideImage) return this.$isColorDark(this.currentSite.secondaryColor) ? 'white' : 'black'
      return this.$isColorDark(this.currentSite.primaryColor) ? 'white' : 'black'
    }
  },
  created () {
    _.extend(this, {
      availabilityBadgeColor,
      availableStock,
      hasReducedOpacity,
      isAvailabilityBadgeVisible,
      isOutOfStock
    })
  },
  mounted () {
    // Calculate price asynchronously
    if (this.displayPrice) {
      setTimeout(async () => {
        const { price } = this.item.getPriceForOrder(this.order, this.isOutOfStock)
        this.itemPrice = price
      }, 0)
    }
  },
  methods: {
    handleTouchHold () {
      if (!this.$register) return

      this.$refs.toggleSalesDisabledDialog?.show()
    },
    handleTouchStart () {
      this.isTouching = true
      setTimeout(() => {
        this.isTouching = false
      }, 500)
    },
    handleClick () {
      const acknowledgeCookies = Cookies.get(this.$constants.cookies.alcoholAcknowledgement)
      const acknowledgeCookiesSiteId = acknowledgeCookies?.siteId

      const dontAskAgainConfirmed = !!acknowledgeCookies && acknowledgeCookiesSiteId === this.currentSite.id

      if (this.item.showAlcoholAcknowledgement && !dontAskAgainConfirmed && !this.$register) {
        this.$refs.alcoholAcknowledgementDialog.show()
      } else {
        this.checkItemSalesRestrictions()
      }
    },
    checkItemSalesRestrictions () {
      this.checkForSalesRestrictions(
        this.item,
        { order: this.order },
        (adminApproved = false) => {
          this.$emit('click', adminApproved)
        }
      )
    },
    itemCount (item) {
      return _.filter(this.order.orderItems.data, orderItem => orderItem.itemOrderItem?.itemId === item.id).length
    },
    notifyAdded (item) {
      this.shown = false // Close the dialog after adding the item to the cart
      if (this.$register) return
      Notify.create({
        message: `${item.name} added to Cart`,
        type: 'positive',
        position: 'bottom',
        group: false,
        timeout: 2500,
        classes: 'added-to-cart-notif'
      })
    }
  }
}
</script>
<style lang="scss">
.added-to-cart-notif {
  margin-bottom: 60px;
  margin-top: -50px;
}
</style>
